import React, { useState, useCallback } from 'react';
import {
 Plus,
 Trash2,
 ChevronDown,
 ChevronRight,
 AlertCircle,
} from 'lucide-react';
import {
 useLLDPractice,
 Attribute,
 Method,
 Parameter,
} from '../../context/LLDPracticeContext';
import { validateMember } from '../../utils/lldValidation';

// ─── Types ──────────────────────────────────────────────────────────────────────

type AccessModifier = 'public' | 'private' | 'protected';

interface SuggestedAttribute {
 name: string;
 type: string;
}

interface SuggestedMethod {
 name: string;
 parameters: { name: string; type: string }[];
 returnType: string;
}

interface SuggestedResponsibilities {
 [entityName: string]: {
  attributes: SuggestedAttribute[];
  methods: SuggestedMethod[];
 };
}

interface ResponsibilityMapperProps {
 suggestedResponsibilities?: SuggestedResponsibilities;
}

// ─── Sub-Components ─────────────────────────────────────────────────────────────

interface AttributeFormState {
 name: string;
 type: string;
 accessModifier: AccessModifier;
}

interface MethodFormState {
 name: string;
 parameters: Parameter[];
 returnType: string;
 accessModifier: AccessModifier;
}

const initialAttributeForm: AttributeFormState = {
 name: '',
 type: 'string',
 accessModifier: 'private',
};

const initialMethodForm: MethodFormState = {
 name: '',
 parameters: [],
 returnType: 'void',
 accessModifier: 'public',
};

const ATTRIBUTE_TYPES = ['string', 'number', 'boolean', 'Date', 'string[]', 'number[]', 'object'];
const RETURN_TYPES = ['void', 'string', 'number', 'boolean', 'Date', 'object'];

// ─── Component ──────────────────────────────────────────────────────────────────

const ResponsibilityMapper: React.FC<ResponsibilityMapperProps> = ({
 suggestedResponsibilities = {},
}) => {
 const { state, dispatch } = useLLDPractice();
 const { entities, responsibilities } = state.workspaceState;
 const { mode } = state.sessionState;

 const isLearnMode = mode === 'learn';

 // Track which entity cards are expanded
 const [expandedEntities, setExpandedEntities] = useState<Set<string>>(
  new Set(entities.map((e) => e.id))
 );

 // Track per-entity form states
 const [attrForms, setAttrForms] = useState<Record<string, AttributeFormState>>({});
 const [methodForms, setMethodForms] = useState<Record<string, MethodFormState>>({});

 // Errors
 const [attrErrors, setAttrErrors] = useState<Record<string, string | null>>({});
 const [methodErrors, setMethodErrors] = useState<Record<string, string | null>>({});

 // Save failure notification
 const [saveError] = useState<string | null>(null);

 // ─── Helpers ──────────────────────────────────────────────────────────────────

 const toggleEntity = useCallback((entityId: string) => {
  setExpandedEntities((prev) => {
   const next = new Set(prev);
   if (next.has(entityId)) {
    next.delete(entityId);
   } else {
    next.add(entityId);
   }
   return next;
  });
 }, []);

 const getAttrForm = (entityId: string): AttributeFormState =>
  attrForms[entityId] || { ...initialAttributeForm };

 const getMethodForm = (entityId: string): MethodFormState =>
  methodForms[entityId] || { ...initialMethodForm };

 const getEntityResponsibility = (entityId: string) => {
  const resp = responsibilities[entityId];
  return {
   entityId,
   attributes: resp?.attributes ?? [],
   methods: resp?.methods ?? [],
  };
 };

 // ─── Attribute Handlers ──────────────────────────────────────────────────────

 const handleAddAttribute = useCallback(
  (entityId: string) => {
   const form = attrForms[entityId] || { ...initialAttributeForm };
   const entityResp = getEntityResponsibility(entityId);
   const existingNames = entityResp.attributes.map((a: Attribute) => a.name);

   const result = validateMember(form.name.trim(), existingNames);
   if (!result.valid) {
    setAttrErrors((prev) => ({ ...prev, [entityId]: result.error || 'Validation failed' }));
    return;
   }

   const newAttribute: Attribute = {
    id: crypto.randomUUID(),
    name: form.name.trim(),
    type: form.type.trim() || 'string',
    accessModifier: form.accessModifier,
    order: entityResp.attributes.length,
   };

   dispatch({
    type: 'ADD_ATTRIBUTE',
    payload: { entityId, attribute: newAttribute },
   });

   setAttrForms((prev) => ({ ...prev, [entityId]: { ...initialAttributeForm } }));
   setAttrErrors((prev) => ({ ...prev, [entityId]: null }));
  },
  [attrForms, responsibilities, dispatch]
 );

 const updateAttrForm = (entityId: string, field: keyof AttributeFormState, value: string) => {
  setAttrForms((prev) => ({
   ...prev,
   [entityId]: { ...(prev[entityId] || { ...initialAttributeForm }), [field]: value },
  }));
  if (attrErrors[entityId]) {
   setAttrErrors((prev) => ({ ...prev, [entityId]: null }));
  }
 };

 // ─── Method Handlers ─────────────────────────────────────────────────────────

 const handleAddMethod = useCallback(
  (entityId: string) => {
   const form = methodForms[entityId] || { ...initialMethodForm };
   const entityResp = getEntityResponsibility(entityId);
   const existingNames = entityResp.methods.map((m: Method) => m.name);

   const result = validateMember(form.name.trim(), existingNames);
   if (!result.valid) {
    setMethodErrors((prev) => ({ ...prev, [entityId]: result.error || 'Validation failed' }));
    return;
   }

   const newMethod: Method = {
    id: crypto.randomUUID(),
    name: form.name.trim(),
    parameters: form.parameters.filter((p) => p.name.trim() !== ''),
    returnType: form.returnType.trim() || 'void',
    accessModifier: form.accessModifier,
    order: entityResp.methods.length,
   };

   dispatch({
    type: 'ADD_METHOD',
    payload: { entityId, method: newMethod },
   });

   setMethodForms((prev) => ({ ...prev, [entityId]: { ...initialMethodForm } }));
   setMethodErrors((prev) => ({ ...prev, [entityId]: null }));
  },
  [methodForms, responsibilities, dispatch]
 );

 const updateMethodForm = (entityId: string, field: keyof MethodFormState, value: string | Parameter[]) => {
  setMethodForms((prev) => ({
   ...prev,
   [entityId]: { ...(prev[entityId] || { ...initialMethodForm }), [field]: value },
  }));
  if (methodErrors[entityId]) {
   setMethodErrors((prev) => ({ ...prev, [entityId]: null }));
  }
 };

 const addParameter = (entityId: string) => {
  const form = methodForms[entityId] || { ...initialMethodForm };
  if (form.parameters.length >= 10) return;
  updateMethodForm(entityId, 'parameters', [
   ...form.parameters,
   { name: '', type: 'string' },
  ]);
 };

 const updateParameter = (entityId: string, index: number, field: 'name' | 'type', value: string) => {
  const form = methodForms[entityId] || { ...initialMethodForm };
  const params = [...form.parameters];
  params[index] = { ...params[index], [field]: value };
  updateMethodForm(entityId, 'parameters', params);
 };

 const removeParameter = (entityId: string, index: number) => {
  const form = methodForms[entityId] || { ...initialMethodForm };
  const params = form.parameters.filter((_, i) => i !== index);
  updateMethodForm(entityId, 'parameters', params);
 };

 // ─── Render ──────────────────────────────────────────────────────────────────

 if (entities.length === 0) {
  return (
   <div className="flex flex-col h-full items-center justify-center">
    <div className="min-h-[180px] flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 dark:border-gray-700 px-8 text-center w-full">
     <AlertCircle className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
     <p className="text-sm text-gray-600 dark:text-gray-400">No entities yet.</p>
     <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">Add them in Entity Discovery first, then map their data and behavior here.</p>
    </div>
   </div>
  );
 }

 return (
  <div className="flex flex-col h-full">
   {/* Save Error Notification */}
   {saveError && (
    <div className="mb-3 px-3 py-2 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-lg flex items-center gap-2">
     <AlertCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />
     <p className="text-sm text-rose-600 dark:text-rose-400">{saveError}</p>
    </div>
   )}

   {/* Entity Cards */}
   <div className="flex-1 overflow-y-auto space-y-3 -mx-1 px-1">
    {entities.map((entity) => {
     const isExpanded = expandedEntities.has(entity.id);
     const entityResp = getEntityResponsibility(entity.id);
     const attrForm = getAttrForm(entity.id);
     const mForm = getMethodForm(entity.id);

     const suggested = isLearnMode
      ? suggestedResponsibilities[entity.name] || null
      : null;

     return (
      <div
       key={entity.id}
       className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
      >
       {/* Entity Card Header (Accordion Toggle) */}
       <button
        onClick={() => toggleEntity(entity.id)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors text-left"
       >
        <div className="flex items-center gap-2.5">
         {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
         ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
         )}
         <span className="w-7 h-7 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300 flex items-center justify-center font-mono text-xs font-semibold flex-shrink-0">
          {entity.name.charAt(0).toUpperCase()}
         </span>
         <span className="font-semibold text-gray-900 dark:text-white">{entity.name}</span>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-500 tabular-nums">
         {entityResp.attributes.length} attr · {entityResp.methods.length} methods
        </span>
       </button>

       {/* Expanded Content */}
       {isExpanded && (
        <div className="px-4 pb-4 pt-1 space-y-5 border-t border-gray-100 dark:border-gray-800">
         {/* ─── Attributes Section ──────────────────────────────────── */}
         <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-2 mt-3">
           Attributes
          </h4>

          {entityResp.attributes.length > 0 && (
           <div className="flex flex-wrap gap-2 mb-2.5">
            {entityResp.attributes.map((attr: Attribute) => (
             <span
              key={attr.id}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg text-xs text-primary-700 dark:text-primary-300 font-mono"
             >
              <span className="opacity-70">
               {attr.accessModifier === 'public' ? '+' : attr.accessModifier === 'private' ? '-' : '#'}
              </span>
              {attr.name}: {attr.type}
             </span>
            ))}
           </div>
          )}

          {/* Add Attribute Form */}
          <div className="flex flex-wrap gap-2 items-start">
           <input
            type="text"
            value={attrForm.name}
            onChange={(e) => updateAttrForm(entity.id, 'name', e.target.value)}
            placeholder="Name"
            className="flex-1 min-w-[100px] px-2.5 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
           />
           <select
            value={attrForm.type}
            onChange={(e) => updateAttrForm(entity.id, 'type', e.target.value)}
            className="w-28 px-2.5 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
           >
            {ATTRIBUTE_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
            {entities.filter((en) => en.id !== entity.id).map((en) => (<option key={en.id} value={en.name}>{en.name}</option>))}
           </select>
           <select
            value={attrForm.accessModifier}
            onChange={(e) => updateAttrForm(entity.id, 'accessModifier', e.target.value)}
            className="w-24 px-2.5 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
           >
            <option value="public">public</option>
            <option value="private">private</option>
            <option value="protected">protected</option>
           </select>
           <button
            onClick={() => handleAddAttribute(entity.id)}
            disabled={!attrForm.name.trim()}
            className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
           >
            <Plus className="w-3.5 h-3.5" /> Add
           </button>
          </div>
          {attrErrors[entity.id] && (
           <p className="text-xs text-rose-500 dark:text-rose-400 mt-1">{attrErrors[entity.id]}</p>
          )}
         </div>

         {/* ─── Methods Section ─────────────────────────────────────── */}
         <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500 mb-2">
           Methods
          </h4>

          {entityResp.methods.length > 0 && (
           <div className="flex flex-wrap gap-2 mb-2.5">
            {entityResp.methods.map((method: Method) => (
             <span
              key={method.id}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg text-xs text-primary-700 dark:text-primary-300 font-mono"
             >
              <span className="opacity-70">
               {method.accessModifier === 'public' ? '+' : method.accessModifier === 'private' ? '-' : '#'}
              </span>
              {method.name}({(method.parameters || []).map((p) => p.name).join(', ')}): {method.returnType}
             </span>
            ))}
           </div>
          )}

          {/* Add Method Form */}
          <div className="space-y-2">
           <div className="flex flex-wrap gap-2 items-start">
            <input
             type="text"
             value={mForm.name}
             onChange={(e) => updateMethodForm(entity.id, 'name', e.target.value)}
             placeholder="Method name"
             className="flex-1 min-w-[120px] px-2.5 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
            />
            <select
             value={mForm.returnType}
             onChange={(e) => updateMethodForm(entity.id, 'returnType', e.target.value)}
             className="w-28 px-2.5 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
            >
             {RETURN_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
             {entities.map((en) => (<option key={en.id} value={en.name}>{en.name}</option>))}
            </select>
            <select
             value={mForm.accessModifier}
             onChange={(e) => updateMethodForm(entity.id, 'accessModifier', e.target.value)}
             className="w-24 px-2.5 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            >
             <option value="public">public</option>
             <option value="private">private</option>
             <option value="protected">protected</option>
            </select>
            <button
             onClick={() => handleAddMethod(entity.id)}
             disabled={!mForm.name.trim()}
             className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
            >
             <Plus className="w-3.5 h-3.5" /> Add
            </button>
           </div>

           {/* Parameters */}
           <div className="ml-1 space-y-1.5">
            {mForm.parameters.map((param, idx) => (
             <div key={idx} className="flex items-center gap-2">
              <span className="text-xs text-gray-400 w-4">{idx + 1}.</span>
              <input
               type="text"
               value={param.name}
               onChange={(e) => updateParameter(entity.id, idx, 'name', e.target.value)}
               placeholder="Param name"
               className="flex-1 min-w-[80px] px-2 py-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
              />
              <input
               type="text"
               value={param.type}
               onChange={(e) => updateParameter(entity.id, idx, 'type', e.target.value)}
               placeholder="Type"
               className="w-20 px-2 py-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
              />
              <button
               onClick={() => removeParameter(entity.id, idx)}
               className="p-1 text-gray-400 hover:text-rose-500 transition-colors"
               title="Remove parameter"
              >
               <Trash2 className="w-3 h-3" />
              </button>
             </div>
            ))}
            <button
             onClick={() => addParameter(entity.id)}
             disabled={mForm.parameters.length >= 10}
             className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
             <Plus className="w-3 h-3" /> Add parameter ({mForm.parameters.length}/10)
            </button>
           </div>
          </div>
          {methodErrors[entity.id] && (
           <p className="text-xs text-rose-500 dark:text-rose-400 mt-1">{methodErrors[entity.id]}</p>
          )}
         </div>

         {/* ─── Learn Mode: Reference Section ─────────────────────── */}
         {isLearnMode && suggested && (
          <div className="mt-1 p-3 bg-primary-50/60 dark:bg-primary-900/10 border border-primary-100 dark:border-primary-900/40 rounded-lg">
           <h4 className="text-xs font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400 mb-2">
            Reference — suggested members
           </h4>
           {(suggested.attributes?.length ?? 0) > 0 && (
            <div className="mb-2">
             <p className="text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">Attributes</p>
             <div className="flex flex-wrap gap-1.5">
              {(suggested.attributes || []).map((sa, idx) => (
               <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-white dark:bg-gray-800 border border-primary-200 dark:border-primary-800 rounded text-xs text-primary-700 dark:text-primary-300 font-mono">
                {sa.name}: {sa.type}
               </span>
              ))}
             </div>
            </div>
           )}
           {(suggested.methods?.length ?? 0) > 0 && (
            <div>
             <p className="text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">Methods</p>
             <div className="flex flex-wrap gap-1.5">
              {(suggested.methods || []).map((sm, idx) => (
               <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-white dark:bg-gray-800 border border-primary-200 dark:border-primary-800 rounded text-xs text-primary-700 dark:text-primary-300 font-mono">
                {sm.name}({(sm.parameters || []).map((p) => p.name).join(', ')}): {sm.returnType}
               </span>
              ))}
             </div>
            </div>
           )}
          </div>
         )}
        </div>
       )}
      </div>
     );
    })}
   </div>
  </div>
 );
};

export default ResponsibilityMapper;
